import { Session } from "@supabase/supabase-js";
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import useSupabaseBrowser from "../hooks/use-supabase-browser";
import { parseJWT } from "../../utils/utils";

interface AuthContextType {
  session: Session | null;
  loading: boolean;
  oid: string;
  authenticated: boolean;
}

// Create a context
const AuthContext = createContext<AuthContextType>({
  session: null,
  loading: true,
  oid: "",
  authenticated: false,
});
// Hook for child components to get the auth object and re-render when it changes
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const supabase = useSupabaseBrowser();
  const [loading, setLoading] = useState<boolean>(true);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);
    });
  }, []);

  // The value passed to the provider's consumers
  const value = {
    session,
    loading,
    oid: session !== null && parseJWT(session.access_token).app_metadata.oid,
    authenticated:
      session !== null && parseJWT(session.access_token).app_metadata.admin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
